import { useCallback, useEffect, useState } from 'react'
import { extractErrorMessage, success, failure } from 'utils/errorHandling'
import useInnovaAxios from 'components/common/hooks/useInnovaAxios'

const useDataBridge = () => {
  const getDataBridgeSettingsRequest = useInnovaAxios({ url: '/admin/email/getEmailSettings' })
  const updateDataBridgeSettingsRequest = useInnovaAxios({ url: '/admin/email/updateEmailSettings' })
  const getDataBridgeDropDownsRequest = useInnovaAxios({ url: '/admin/email/getDropDowns' })
  const getDataBridgeHistoryRequest = useInnovaAxios({ url: '/dataFeed/getEmailFeed' })

  const [isLoading, setIsLoading] = useState(false)
  const [isLoadingHistory, setIsLoadingHistory] = useState(false)
  const [isUpdating, setIsUpdating] = useState(false)
  const [isLoadingDropDowns, setIsLoadingDropDowns] = useState(false)

  const [dropDowns, setDropDowns] = useState({ depthUnits: [], mudUnits: [], rules: [], dateFormat: [] })

  /* ============================================================
       Get data bridge settings
    ============================================================ */

  const fetchDataBridgeSettings = useCallback(
    async (organization) => {
      if (!organization || organization === '') {
        return failure('Invalid organization ' + organization)
      }

      setIsLoading(true)
      try {
        const res = await getDataBridgeSettingsRequest({ organization })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to fetch databridge settings'))
        }

        return success('DataBridge settings loaded', res?.data)
      } finally {
        setIsLoading(false)
      }
    },
    [getDataBridgeSettingsRequest],
  )

  /* ============================================================
       Get data bridge history
    ============================================================ */

  const fetchDataBridgeHistory = useCallback(async () => {
    setIsLoadingHistory(true)
    try {
      const res = await getDataBridgeHistoryRequest()

      if (res?.error) {
        return failure(extractErrorMessage(res.error, 'Failed to fetch databridge history'))
      }

      return success('DataBridge history loaded', res?.data)
    } finally {
      setIsLoadingHistory(false)
    }
  }, [getDataBridgeHistoryRequest])

  /* ============================================================
       Update data bridge settings
    ============================================================ */

  const updateDataBridgeSettings = useCallback(
    async (organization, newSettings) => {
      if (!organization || organization === '') {
        return failure('Invalid organization ' + organization)
      }

      if (!Array.isArray(newSettings)) {
        return failure('Invalid settings')
      }

      setIsUpdating(true)
      try {
        const res = await updateDataBridgeSettingsRequest({
          organization,
          settings: JSON.stringify({ settings: newSettings }),
        })

        if (res?.error) {
          return failure(extractErrorMessage(res.error, 'Failed to update databridge settings'))
        }

        return success('DataBridge settings updated')
      } finally {
        setIsUpdating(false)
      }
    },
    [updateDataBridgeSettingsRequest],
  )

  /* ============================================================
       Get drop downs
    ============================================================ */

  const getDatabridgeDropDowns = useCallback(async () => {
    setIsLoadingDropDowns(true)
    try {
      const res = await getDataBridgeDropDownsRequest()

      if (res?.error) {
        return failure(extractErrorMessage(res.error, 'Failed to get databridge drop downs'))
      }

      if (res?.data) {
        setDropDowns({
          depthUnits: res.data.DepthUnits || [],
          mudUnits: res.data.MudUnits || [],
          rules: res.data.Rules || [],
          dateFormat: res.data.DateFormat || [],
        })
      }

      return success('Databridge drop downs loaded', res?.data)
    } finally {
      setIsLoadingDropDowns(false)
    }
  }, [getDataBridgeDropDownsRequest])

  useEffect(() => {
    getDatabridgeDropDowns()
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  return {
    isLoading,
    isUpdating,
    isLoadingDropDowns,
    isLoadingHistory,
    fetchDataBridgeSettings,
    updateDataBridgeSettings,
    getDatabridgeDropDowns,
    fetchDataBridgeHistory,
    dropDowns,
  }
}

export default useDataBridge
